import * as THREE from "three";
import { buildTowerModel } from "./views";
import { reduceModel } from "./hubLod";
import { HUB_BUDGET } from "./hubBudget";
import { HubLayout } from "./hubLayout";

/**
 * The next tower to unlock, standing on its stand as a pale translucent ghost (the Castle concept's graft on the
 * lobby): the player sees the empty place in the line-up and what will fill it. The hub UI hangs its label
 * ("Fire · Level 2") from `HubScene.anchorOnScreen({ tower })`.
 *
 * The model is the tower's tier-1 look at hub detail, held still in its rest pose (a ghost does not idle). It draws
 * twice: a depth-only pass first, so the translucent pass shows only the ghost's front surface instead of every
 * wall and beam behind it at once, then the translucent pass itself. Neither casts a shadow.
 */
export class HubGhost {
  /** How much of the ghost shows through (0 invisible, 1 solid). */
  static readonly Opacity = 0.42;
  /** A cool pale tint and a faint self-glow, so it reads as "not built yet" on any biome's ground. */
  static readonly Tint = 0xd8ecff;
  static readonly Glow = 0x2c4a66;
  /** Draw order of the passes: the depth pass right after the opaque world, the colour pass with the transparents. */
  static readonly DepthOrder = 1;

  readonly root: THREE.Object3D;
  readonly box: THREE.Box3;
  private readonly geometries: THREE.BufferGeometry[];
  private readonly materials: THREE.Material[] = [];

  constructor(readonly towerId: string, at: THREE.Vector3) {
    const model = buildTowerModel(towerId, false, 1);
    this.root = model.storeys;
    this.root.position.copy(at);
    this.geometries = reduceModel(this.root, HUB_BUDGET.ModelLod, HUB_BUDGET.LodMinTriangles);
    const depth = new THREE.MeshBasicMaterial({ colorWrite: false });
    this.materials.push(depth);
    const meshes: THREE.Mesh[] = [];
    // a worn skin kit's props need their own material (bone atlas, GPU motion): the ghost is the tower's body alone
    this.root.traverse((o) => { if (o.userData.tdKit === true) o.visible = false; else if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh); });
    for (const mesh of meshes) {
      const src = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshStandardMaterial;
      const ghost = new THREE.MeshStandardMaterial({
        map: src.map ?? null, color: HubGhost.Tint, emissive: HubGhost.Glow, roughness: 0.9, metalness: 0,
        transparent: true, opacity: HubGhost.Opacity, depthWrite: false,
      });
      this.materials.push(ghost);
      mesh.material = ghost;
      mesh.castShadow = mesh.receiveShadow = false;
      const pre = mesh.clone(false);                         // a SkinnedMesh clone shares the skeleton and bind matrix
      pre.material = depth;
      pre.renderOrder = HubGhost.DepthOrder;
      mesh.parent!.add(pre);
    }
    this.box = HubLayout.modelBox(this.root);
  }

  dispose(): void {
    this.root.removeFromParent();
    for (const g of this.geometries) g.dispose();
    for (const m of this.materials) m.dispose();
  }
}
